import { useCallback, useEffect, useRef, useState } from "react";
import { CloudDownload, DatabaseBackup, Download, Loader2, RotateCcw, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { downloadPdf } from "@/lib/download";
import { fmtDate } from "@/lib/format";
import { RestoreDialog } from "@/components/super/RestoreDialog";
import { SyncDialog } from "@/components/super/SyncDialog";
import { BackupCustomersDialog } from "@/components/super/BackupCustomersDialog";

const mb = (n) => (n < 1024 * 1024
  ? `${Math.max(1, Math.round(n / 1024))} kB`
  : `${(n / 1024 / 1024).toLocaleString("nb-NO", { maximumFractionDigits: 1, minimumFractionDigits: 1 })} MB`);

const BackupRow = ({ b, highlight, onRestore }) => (
  <tr className={`border-t border-slate-100 ${highlight ? "bg-sky-50" : ""}`} data-testid={`backup-row-${b.id}`}>
    <td className="py-3 px-4 font-num text-xs text-slate-600 whitespace-nowrap">{fmtDate(b.created_at)}</td>
    <td className="py-3 px-4 text-slate-700">{b.customers} havnekontor</td>
    <td className="py-3 px-4 font-num text-slate-700">{mb(b.size)}</td>
    <td className="py-3 px-4 text-xs text-slate-500">{b.trigger === "cron" ? "Automatisk" : "Manuell"}</td>
    <td className="py-3 px-4 text-right whitespace-nowrap space-x-2">
      <Button size="sm" variant="outline" onClick={() => onRestore(b)} data-testid={`backup-restore-${b.id}`}>
        <RotateCcw className="w-3.5 h-3.5 mr-1" />Gjenopprett
      </Button>
      <Button size="sm" variant="outline" data-testid={`backup-download-${b.id}`}
        onClick={() => downloadPdf(`/admin/backups/${b.id}/download`, {}, b.filename).catch((e) => toast.error(errMsg(e)))}>
        <Download className="w-3.5 h-3.5 mr-1" />Last ned
      </Button>
    </td>
  </tr>
);

export const BackupPanel = ({ highlight, onRestored, customers = [] }) => {
  const [items, setItems] = useState([]);
  const [sync, setSync] = useState(false);
  const [syncInfo, setSyncInfo] = useState(null);
  const loadSync = useCallback(() => api.get("/admin/sync/status").then((r) => setSyncInfo(r.data)).catch(() => {}), []);
  useEffect(() => {
    loadSync();
  }, [loadSync]);
  const [busy, setBusy] = useState(false);
  const [src, setSrc] = useState(null);
  const [pick, setPick] = useState(null);
  const closeSrc = useCallback(() => setSrc(null), []);
  const closePick = useCallback(() => setPick(null), []);
  const input = useRef(null);

  const load = useCallback(() => api.get("/admin/backups").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => {
    load();
  }, [load]);

  const runNow = async () => {
    setBusy(true);
    try {
      await api.post("/admin/backups");
      toast.success("Backup er laget");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg" data-testid="backup-panel">
      <div className="flex flex-wrap items-start justify-between gap-3 p-4 border-b border-slate-100">
        <p className="text-sm text-slate-500 max-w-xl">Hver mandag kl. 03:00 lages en zip med én zip-fil per havnekontor. Lenke sendes på e-post. Backupene tas vare på i 52 uker.
          {syncInfo && !syncInfo.available && <span className="block mt-1 text-xs text-slate-400" data-testid="sync-preview-note">Importen henter fra {syncInfo.source}, som er denne serveren. På live-serveren angir du adressen til preview under «Importer fra preview».</span>}
        </p>
        <div className="flex flex-wrap gap-2">
          {syncInfo && <Button variant="outline" size="sm" onClick={() => setSync(true)} data-testid="sync-open-button"><CloudDownload className="w-4 h-4 mr-1" />Importer fra preview</Button>}
          <input ref={input} type="file" accept=".zip,application/zip" className="hidden" data-testid="restore-file-input"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) setSrc({ file: f }); e.target.value = ""; }} />
          <Button variant="outline" size="sm" onClick={() => input.current?.click()} data-testid="restore-upload-button"><Upload className="w-4 h-4 mr-1" />Last opp kunde-zip</Button>
          <Button size="sm" onClick={runNow} disabled={busy} data-testid="backup-now-button">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <DatabaseBackup className="w-4 h-4 mr-1" />}Ta backup nå
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-wide text-slate-500">
              <th className="py-2.5 px-4 font-medium">Tidspunkt</th><th className="py-2.5 px-4 font-medium">Innhold</th>
              <th className="py-2.5 px-4 font-medium">Størrelse</th><th className="py-2.5 px-4 font-medium">Type</th>
              <th className="py-2.5 px-4 font-medium text-right">Handlinger</th>
            </tr>
          </thead>
          <tbody>
            {items.map((b) => <BackupRow key={b.id} b={b} highlight={b.id === highlight} onRestore={setPick} />)}
            {!items.length && <tr><td colSpan={5} className="py-10 text-center text-slate-500" data-testid="backup-empty">Ingen backup ennå.</td></tr>}
          </tbody>
        </table>
      </div>
      <BackupCustomersDialog backup={pick} onClose={closePick} onPick={(s) => { setPick(null); setSrc(s); }} />
      <RestoreDialog src={src} onClose={closeSrc} onRestored={onRestored} />
      <SyncDialog open={sync} onClose={() => setSync(false)} customers={customers} onDone={onRestored} info={syncInfo} onSource={loadSync} />
    </div>
  );
};
